import { useMutationState } from '@tanstack/react-query';

/**
 * Shared TanStack Query mutation key for the project-anchored "create chat"
 * mutation (`useCreateChatMutation` in feature-project-page's
 * `useProjectChats.ts`). Owned here — rather than by feature-project-page —
 * so `useEpiChatPage`'s subscribe effect can watch for a matching in-flight
 * mutation via `useMutationState` without feature-epi-chat depending on
 * feature-project-page (the reverse of the existing NX dependency
 * direction: feature-project-page already imports from feature-epi-chat).
 */
export const createChatMutationKey = (projectId: string) => ['createChat', projectId] as const;

/**
 * The subset of the create-chat mutation's variables `usePendingChatCreation`
 * reads. `CreateChatSessionBody` (feature-project-page) extends this so
 * renaming or dropping `id` there fails to compile instead of silently
 * disabling the gate below.
 */
export interface CreateChatMutationVariables {
  /**
   * Client-minted chat id used to correlate a still-in-flight `createChat`
   * mutation with the chat id in the URL — see `usePendingChatCreation` below.
   */
  id?: string;
}

/**
 * True while a `createChat` mutation for this exact chat id is still in
 * flight.
 *
 * Backs the fix for the optimistic-create race: the FE mints the chat id
 * and navigates to `?chat=<id>` before the create POST resolves (see
 * `ChatPanelEmptyState.mintChat`), so without this gate the socket
 * `subscribe` can reach the BE before the chat row commits. The BE's own
 * lookup retry there is bounded (~1.5s) and real creation latency can
 * exceed it, surfacing a terminal "You don't have access to this chat."
 * for a chat that's perfectly valid moments later.
 */
export const usePendingChatCreation = (chatId: string | null | undefined): boolean => {
  const pendingIds = useMutationState({
    filters: { mutationKey: ['createChat'], status: 'pending' },
    select: (mutation) => (mutation.state.variables as CreateChatMutationVariables | undefined)?.id,
  });
  return chatId != null && pendingIds.includes(chatId);
};
